import MiniSearch, { type SearchResult } from 'minisearch';
import { normaliseTerm, SEARCH_OPTIONS, type HelpSearchDoc } from './searchOptions';
import { stripMediaPlaceholders } from '@tradeoutlet/shared';

export interface HelpHit {
  key: string; anchor: string | null; heading: string | null; title: string; area: string;
  /** HTML-escaped text with <mark> around the matched words. */
  snippet: string;
  /** Title with matches marked (escaped). */
  titleHtml: string;
  score: number;
  /** Where it comes from: the built-in guide ('help') or something your company wrote ('company'). Default 'help'. */
  source?: 'help' | 'company';
  /** Set for results outside the guide (knowledge-base articles): where to open it. */
  href?: string;
}

let index: Promise<MiniSearch<HelpSearchDoc>> | null = null;
/** The prebuilt index is a separate chunk, fetched the first time someone searches. */
export function loadHelpIndex(): Promise<MiniSearch<HelpSearchDoc>> {
  index ??= import('virtual:help-index').then(m => MiniSearch.loadJSON<HelpSearchDoc>(m.default, SEARCH_OPTIONS));
  return index;
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Wrap words whose normalised form starts with (or fuzzily is) one of the matched index terms. */
export function markTerms(text: string, terms: string[], window = 0): string {
  if (!text) return '';
  const words = [...text.matchAll(/[\p{L}\p{N}][\p{L}\p{N}'’-]*/gu)];
  const hit = (w: string) => { const n = normaliseTerm(w.replace(/['’]s$/, '')); return Boolean(n && terms.some(t => n.startsWith(t) || (t.length > 3 && t.startsWith(n) && n.length >= t.length - 1))); };
  const hits = words.filter(m => hit(m[0]));
  let start = 0; let end = text.length;
  if (window && text.length > window) {
    const first = hits[0]?.index ?? 0;
    start = Math.max(0, first - Math.round(window * 0.3));
    const sp = text.lastIndexOf(' ', start); if (start > 0 && sp > 0 && start - sp < 20) start = sp + 1;
    end = Math.min(text.length, start + window);
    const ep = text.indexOf(' ', end); if (ep > 0 && ep - end < 20) end = ep;
  }
  let out = ''; let pos = start;
  for (const m of hits) {
    const i = m.index ?? 0;
    if (i < start || i + m[0].length > end) continue;
    out += esc(text.slice(pos, i)) + `<mark>${esc(m[0])}</mark>`;
    pos = i + m[0].length;
  }
  out += esc(text.slice(pos, end));
  return (start > 0 ? '…' : '') + out + (end < text.length ? '…' : '');
}

/** Best matching section per article, in score order. `allow` filters by article key (permissions). */
export function searchHelp(ms: MiniSearch<HelpSearchDoc>, q: string, allow: (key: string) => boolean, limit = 20): HelpHit[] {
  const query = q.trim();
  if (!query) return [];
  let results: SearchResult[] = ms.search(query);
  // AND finds nothing when one word is off-topic; fall back to OR so the closest articles still show.
  if (!results.length) results = ms.search(query, { combineWith: 'OR' });
  const best = new Map<string, SearchResult>();
  for (const r of results) {
    const key = r.key as string;
    if (!allow(key)) continue;
    const cur = best.get(key);
    // Article score: the best section, nudged up when several sections match.
    if (!cur) best.set(key, { ...r, score: r.score });
    else cur.score += r.score * 0.15;
  }
  return [...best.values()].sort((a, b) => b.score - a.score).slice(0, limit).map(r => {
    const terms = [...new Set([...r.terms, ...r.queryTerms].map(t => normaliseTerm(t) ?? t))];
    return {
      key: r.key as string, anchor: (r.anchor as string | null) ?? null, heading: (r.heading as string | null) ?? null, title: r.title as string, area: r.area as string,
      snippet: markTerms((r.text as string) ?? '', terms, 180), titleHtml: markTerms(r.title as string, terms), score: r.score,
    };
  });
}

/** Workspace articles (from the API) are few: index them on the fly with the same rules. */
export function searchCustom<T extends { id: string; title: string; summary: string | null; body_text: string }>(items: T[], q: string, limit = 10): (HelpHit & { item: T })[] {
  if (!q.trim() || !items.length) return [];
  const ms = new MiniSearch<HelpSearchDoc>(SEARCH_OPTIONS);
  ms.addAll(items.map(i => ({ id: i.id, key: `company/${i.id}`, anchor: null, heading: null, text: stripMediaPlaceholders(i.body_text), title: i.title, summary: i.summary ?? '', keywords: '', area: 'company' })));
  const byId = new Map(items.map(i => [i.id, i]));
  return searchHelp(ms, q, () => true, limit).map(h => ({ ...h, source: 'company' as const, item: byId.get(h.key.slice('company/'.length))! })).filter(h => h.item);
}
